export type GlossaryTerm = {
  term: string
  definition: string
  slug: string
  seeAlso?: string[]
  relatedPage?: { href: string; label: string }
}

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  // ── A ──────────────────────────────────────────────────────────────
  {
    term: "Asking Price",
    slug: "asking-price",
    definition:
      "The price at which a property is initially marketed. It signals the vendor's expectation and is the starting point for negotiations. In England and Wales, the asking price is not legally binding until contracts are exchanged.",
    seeAlso: ["guide-price", "offer"],
  },
  {
    term: "Aspect Ratio",
    slug: "aspect-ratio",
    definition:
      "The proportional relationship between the width and height of a video or image. Common aspect ratios for property video include 9:16 (portrait, for Reels and TikTok), 16:9 (landscape, for YouTube), and 1:1 (square, for feeds and LinkedIn).",
    relatedPage: { href: "/features", label: "See our video features" },
  },
  {
    term: "AST (Assured Shorthold Tenancy)",
    slug: "assured-shorthold-tenancy",
    definition:
      "The most common type of tenancy agreement in England and Wales. An AST typically runs for 6 or 12 months and gives the landlord the right to regain possession at the end of the fixed term. Most residential lettings in the UK are ASTs.",
    seeAlso: ["tenant-referencing", "deposit-protection"],
  },

  // ── B ──────────────────────────────────────────────────────────────
  {
    term: "Brand Guidelines",
    slug: "brand-guidelines",
    definition:
      "A set of rules defining how a brand should be presented visually and verbally. For estate agents, this includes logo usage, colours, fonts, and tone of voice. Consistent branding across property videos builds recognition and trust with vendors and buyers.",
  },
  {
    term: "Buyer Persona",
    slug: "buyer-persona",
    definition:
      "A semi-fictional profile of your ideal property buyer based on demographics, behaviour, and motivations. Understanding buyer personas helps agents tailor video content and marketing messages to the right audience for each listing.",
  },

  // ── C ──────────────────────────────────────────────────────────────
  {
    term: "Call to Action (CTA)",
    slug: "call-to-action",
    definition:
      "A prompt that encourages the viewer to take a specific action, such as 'Book a viewing' or 'Contact the agent'. Every property video should include a clear CTA to convert viewers into enquiries.",
    relatedPage: { href: "/how-it-works", label: "How it works" },
  },
  {
    term: "Caption",
    slug: "caption",
    definition:
      "The text that accompanies a social media post. For property marketing, captions should include a compelling hook, key property details, relevant hashtags, and a call to action. Well-crafted captions significantly increase engagement on property video posts.",
    seeAlso: ["hashtag-strategy", "social-media-marketing"],
  },
  {
    term: "Chain",
    slug: "chain",
    definition:
      "A sequence of linked property transactions where each sale depends on another. A chain forms when a buyer needs to sell their current property to fund the purchase of a new one. Chains can cause delays and are a common risk in property sales.",
    seeAlso: ["exchange-of-contracts", "completion"],
  },
  {
    term: "Click-Through Rate (CTR)",
    slug: "click-through-rate",
    definition:
      "The percentage of people who click on a link after seeing it. In property marketing, CTR measures how effectively a listing, video, or social post drives traffic to your website or portal listing. Higher CTR means your marketing is compelling enough to prompt action.",
    seeAlso: ["engagement-rate", "impression"],
  },
  {
    term: "Comparable Evidence (Comps)",
    slug: "comparable-evidence",
    definition:
      "Recent sale prices of similar properties in the same area, used to determine a realistic asking price or valuation. Agents use comps during market appraisals to justify their recommended asking price to vendors.",
    seeAlso: ["market-appraisal", "asking-price"],
  },
  {
    term: "Completion",
    slug: "completion",
    definition:
      "The final stage of a property transaction when ownership is legally transferred from seller to buyer. On completion day, the buyer's solicitor transfers funds, keys are handed over, and the sale is officially done.",
    seeAlso: ["exchange-of-contracts", "sstc", "conveyancing"],
  },
  {
    term: "Content Calendar",
    slug: "content-calendar",
    definition:
      "A schedule that plans what content to publish, when, and on which platforms. Estate agents who maintain a content calendar for their property videos and social posts are more consistent and see better engagement over time.",
    seeAlso: ["social-media-marketing", "multi-platform-publishing"],
  },
  {
    term: "Conveyancing",
    slug: "conveyancing",
    definition:
      "The legal process of transferring property ownership from one party to another. Conveyancing is carried out by solicitors or licensed conveyancers and covers searches, contracts, and completion.",
    seeAlso: ["exchange-of-contracts", "completion"],
  },

  // ── D ──────────────────────────────────────────────────────────────
  {
    term: "Deposit Protection",
    slug: "deposit-protection",
    definition:
      "A legal requirement in England and Wales for landlords to protect tenant deposits in a government-approved scheme within 30 days of receipt. The three approved schemes are the DPS, MyDeposits, and TDS.",
    seeAlso: ["assured-shorthold-tenancy", "tenant-referencing"],
  },
  {
    term: "Digital Marketing",
    slug: "digital-marketing",
    definition:
      "Marketing that uses online channels to reach potential buyers, tenants, or vendors. For estate agents, this includes property portals, social media, email campaigns, SEO, and video marketing.",
    seeAlso: ["social-media-marketing", "search-engine-optimisation"],
    relatedPage: { href: "/features", label: "See our features" },
  },
  {
    term: "Drone Footage",
    slug: "drone-footage",
    definition:
      "Aerial video captured using an unmanned aerial vehicle (drone). Drone footage is used in property marketing to showcase the exterior, surrounding area, and plot size of a property. Commercial drone operation in the UK requires a valid flyer ID and operator ID from the CAA.",
  },
  {
    term: "Dwell Time",
    slug: "dwell-time",
    definition:
      "The amount of time a user spends on a webpage before returning to search results. Longer dwell time signals to search engines that content is valuable. Property listings with video tend to have significantly higher dwell times than those with photos alone.",
    seeAlso: ["search-engine-optimisation"],
  },

  // ── E ──────────────────────────────────────────────────────────────
  {
    term: "Engagement Rate",
    slug: "engagement-rate",
    definition:
      "A metric that measures how actively an audience interacts with content, typically calculated as the total interactions (likes, comments, shares, saves) divided by reach or impressions. Property videos consistently achieve higher engagement rates than static image posts.",
    seeAlso: ["click-through-rate", "organic-reach"],
  },
  {
    term: "EPC (Energy Performance Certificate)",
    slug: "energy-performance-certificate",
    definition:
      "A legal document that rates a property's energy efficiency from A (most efficient) to G (least efficient). An EPC is required before a property can be marketed for sale or rent in England and Wales and is valid for 10 years.",
  },
  {
    term: "Exchange of Contracts",
    slug: "exchange-of-contracts",
    definition:
      "The point in a property transaction when the sale becomes legally binding. Both parties sign identical contracts, and the buyer typically pays a deposit of 10%. After exchange, neither party can withdraw without financial penalty.",
    seeAlso: ["completion", "conveyancing", "sstc"],
  },

  // ── F ──────────────────────────────────────────────────────────────
  {
    term: "First-Time Buyer (FTB)",
    slug: "first-time-buyer",
    definition:
      "A person purchasing their first residential property. First-time buyers in England and Northern Ireland may benefit from reduced Stamp Duty rates. They are an important demographic for agents marketing starter homes and new-build developments.",
    seeAlso: ["stamp-duty"],
  },
  {
    term: "Floor Plan",
    slug: "floor-plan",
    definition:
      "A scaled diagram showing the layout of a property, including room dimensions, doors, and windows. Floor plans are a core component of property marketing and help buyers understand the space before visiting. Properties marketed with floor plans receive more enquiries.",
    seeAlso: ["property-particulars"],
  },
  {
    term: "Freehold",
    slug: "freehold",
    definition:
      "A type of property ownership where the owner owns the building and the land it sits on outright, with no time limit. Freehold is the most complete form of ownership in England and Wales, as opposed to leasehold.",
    seeAlso: ["leasehold"],
  },

  // ── G ──────────────────────────────────────────────────────────────
  {
    term: "Gazumping",
    slug: "gazumping",
    definition:
      "When a seller accepts a higher offer from a different buyer after already accepting an offer. Gazumping is legal in England and Wales because the sale is not binding until exchange of contracts. It is one of the most common frustrations in the property market.",
    seeAlso: ["exchange-of-contracts", "offer"],
  },
  {
    term: "Geo-Targeting",
    slug: "geo-targeting",
    definition:
      "Delivering content or advertisements to users based on their geographic location. Estate agents use geo-targeting in social media ads to reach potential buyers within a specific radius of a listed property.",
    seeAlso: ["social-media-marketing"],
  },
  {
    term: "Guide Price",
    slug: "guide-price",
    definition:
      "An indication of the seller's minimum acceptable price, commonly used in auction or best-and-final-offers scenarios. A guide price is not the same as an asking price; it is intended to give potential buyers a realistic starting point for bidding.",
    seeAlso: ["asking-price"],
  },

  // ── H ──────────────────────────────────────────────────────────────
  {
    term: "Hashtag Strategy",
    slug: "hashtag-strategy",
    definition:
      "A planned approach to using hashtags on social media posts to increase discoverability. For property marketing, effective hashtag strategies combine location-specific tags (e.g. #BristolProperty), industry tags (#EstateAgent), and content-type tags (#PropertyVideo).",
    seeAlso: ["social-media-marketing", "organic-reach"],
  },
  {
    term: "HDR Photography",
    slug: "hdr-photography",
    definition:
      "High Dynamic Range photography combines multiple exposures of the same scene to capture detail in both bright and dark areas. HDR is widely used in property photography to ensure windows are not blown out and interiors are well-lit and inviting.",
  },
  {
    term: "HHSRS (Housing Health and Safety Rating System)",
    slug: "hhsrs",
    definition:
      "A risk-based evaluation tool used by local authorities in England and Wales to assess potential health and safety hazards in residential properties. Landlords and letting agents must ensure rental properties meet HHSRS standards.",
  },

  // ── I ──────────────────────────────────────────────────────────────
  {
    term: "Impression",
    slug: "impression",
    definition:
      "A single instance of content being displayed to a user. In social media and digital marketing, impressions count how many times a post or video was shown, regardless of whether the user interacted with it. High impressions with low engagement suggests the content is visible but not compelling.",
    seeAlso: ["engagement-rate", "reach"],
  },
  {
    term: "Instagram Reels",
    slug: "instagram-reels",
    definition:
      "Short-form vertical video content on Instagram, up to 90 seconds long. Reels receive prioritised distribution in the Instagram algorithm, making them one of the most effective channels for property video marketing.",
    seeAlso: ["tiktok", "youtube-shorts", "multi-platform-publishing"],
    relatedPage: { href: "/features", label: "See our features" },
  },
  {
    term: "Instruction",
    slug: "instruction",
    definition:
      "The formal agreement between a property owner and an estate agent to market and sell (or let) their property. Winning instructions is the core revenue driver for estate agents. Video marketing is increasingly the differentiator that wins instructions at market appraisals.",
    seeAlso: ["market-appraisal", "sole-agency"],
    relatedPage: { href: "/why-quiteright", label: "Why QuiteRight" },
  },

  // ── J ──────────────────────────────────────────────────────────────
  {
    term: "Joint Agency",
    slug: "joint-agency",
    definition:
      "An arrangement where two estate agents are instructed simultaneously to sell the same property, with a pre-agreed commission split. Joint agency typically results in a higher total fee for the vendor but can broaden the property's exposure.",
    seeAlso: ["sole-agency", "multi-agency"],
  },
  {
    term: "Joint Sole Agency",
    slug: "joint-sole-agency",
    definition:
      "An arrangement where two agents share the instruction exclusively, meaning no other agents can be appointed. The commission is shared between the two agents regardless of which one finds the buyer.",
    seeAlso: ["sole-agency", "joint-agency"],
  },

  // ── K ──────────────────────────────────────────────────────────────
  {
    term: "Kerb Appeal",
    slug: "kerb-appeal",
    definition:
      "The visual attractiveness of a property when viewed from the street. First impressions matter in property marketing: good kerb appeal can significantly increase buyer interest. The opening shot of a property video often determines whether viewers keep watching.",
    seeAlso: ["property-video"],
  },

  // ── L ──────────────────────────────────────────────────────────────
  {
    term: "Lead Generation",
    slug: "lead-generation",
    definition:
      "The process of attracting and capturing interest from potential buyers, tenants, or vendors. For estate agents, lead generation channels include property portals, social media, video marketing, open days, and referrals.",
    seeAlso: ["digital-marketing", "social-media-marketing"],
    relatedPage: { href: "/features", label: "See our features" },
  },
  {
    term: "Leasehold",
    slug: "leasehold",
    definition:
      "A form of property ownership where the buyer owns the property for a fixed period (the lease term) but not the land it sits on. The land is owned by the freeholder. Leasehold is common for flats in England and Wales. Lease length affects property value and mortgage eligibility.",
    seeAlso: ["freehold"],
  },
  {
    term: "Listing",
    slug: "listing",
    definition:
      "A property that has been placed on the market for sale or rent. In the context of estate agency, a listing includes the property details, photographs, floor plan, price, and agent contact information. With QuiteRight, any listing can be turned into a professional video ad.",
    seeAlso: ["property-particulars", "property-portal"],
    relatedPage: { href: "/how-it-works", label: "How it works" },
  },

  // ── M ──────────────────────────────────────────────────────────────
  {
    term: "Market Appraisal",
    slug: "market-appraisal",
    definition:
      "A free, informal assessment of a property's current market value, carried out by an estate agent. Market appraisals are the primary opportunity for agents to win instructions. Arriving with a professional property video demonstrates marketing competence and helps convert appraisals into instructions.",
    seeAlso: ["instruction", "comparable-evidence"],
    relatedPage: { href: "/why-quiteright", label: "Why QuiteRight" },
  },
  {
    term: "Mortgage in Principle (MIP)",
    slug: "mortgage-in-principle",
    definition:
      "A conditional agreement from a lender indicating how much they would be willing to lend, subject to a full application and valuation. Buyers with an MIP are considered more serious and are taken more seriously by agents and vendors.",
  },
  {
    term: "Multi-Agency",
    slug: "multi-agency",
    definition:
      "An arrangement where multiple estate agents are each independently instructed to sell the same property. Only the agent who finds the buyer earns the commission, which is typically higher than sole agency to reflect the competitive arrangement.",
    seeAlso: ["sole-agency", "joint-agency"],
  },
  {
    term: "Multi-Platform Publishing",
    slug: "multi-platform-publishing",
    definition:
      "The practice of distributing content across multiple social media platforms simultaneously. Publishing property videos to Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn maximises reach and ensures your listings are seen wherever buyers are scrolling.",
    seeAlso: ["instagram-reels", "tiktok", "youtube-shorts"],
    relatedPage: { href: "/features", label: "See our features" },
  },

  // ── N ──────────────────────────────────────────────────────────────
  {
    term: "New Build",
    slug: "new-build",
    definition:
      "A newly constructed property that has not been previously occupied. New builds often come with NHBC warranties and may qualify for government schemes. They represent a significant segment of the property market and respond well to video marketing.",
  },

  // ── O ──────────────────────────────────────────────────────────────
  {
    term: "Offer",
    slug: "offer",
    definition:
      "A formal expression of interest to purchase a property at a stated price. In England and Wales, offers are not legally binding until exchange of contracts. Estate agents are legally required to pass on all offers to the vendor.",
    seeAlso: ["exchange-of-contracts", "gazumping"],
  },
  {
    term: "OnTheMarket",
    slug: "onthemarket",
    definition:
      "A UK property portal launched in 2015 as an alternative to Rightmove and Zoopla. Agents list properties on OnTheMarket to reach an additional audience of buyers and tenants beyond the two dominant portals.",
    seeAlso: ["rightmove", "zoopla", "property-portal"],
  },
  {
    term: "Open House",
    slug: "open-house",
    definition:
      "A scheduled viewing event where multiple potential buyers visit a property during a set time window. Open days generate competitive interest and can lead to faster offers. Promoting open days with video on social media increases attendance.",
    seeAlso: ["viewings"],
  },
  {
    term: "Organic Reach",
    slug: "organic-reach",
    definition:
      "The number of unique users who see your content without paid promotion. On most social platforms, video content receives significantly higher organic reach than static images, making property video a cost-effective marketing channel.",
    seeAlso: ["reach", "engagement-rate"],
  },

  // ── P ──────────────────────────────────────────────────────────────
  {
    term: "Property Particulars",
    slug: "property-particulars",
    definition:
      "The detailed written and visual description of a property for sale or rent, including address, price, photographs, floor plan, room descriptions, and EPC rating. Particulars are the foundation of all property marketing materials, including video.",
    seeAlso: ["listing", "floor-plan", "energy-performance-certificate"],
  },
  {
    term: "Property Portal",
    slug: "property-portal",
    definition:
      "An online platform where properties are listed for sale or rent. The three major UK portals are Rightmove, Zoopla, and OnTheMarket. Portals are the primary discovery channel for buyers, but agents increasingly use social media and video to supplement portal exposure.",
    seeAlso: ["rightmove", "zoopla", "onthemarket"],
  },
  {
    term: "Property Video",
    slug: "property-video",
    definition:
      "A short video showcasing a property for sale or rent, typically 15\u201360 seconds long. Property videos combine photographs, text overlays, voiceover, and music to create an engaging marketing asset for social media and property portals. Properties marketed with video receive significantly more enquiries.",
    seeAlso: ["video-tour", "video-walkthrough", "voiceover"],
    relatedPage: { href: "/how-it-works", label: "How it works" },
  },

  // ── R ──────────────────────────────────────────────────────────────
  {
    term: "Reach",
    slug: "reach",
    definition:
      "The total number of unique users who have seen a piece of content. Reach is a key metric in property marketing because it indicates how many potential buyers or tenants have been exposed to a listing.",
    seeAlso: ["impression", "organic-reach"],
  },
  {
    term: "Rightmove",
    slug: "rightmove",
    definition:
      "The UK's largest property portal, with over 1.5 billion visits per year. Most estate agents list on Rightmove as their primary marketing channel. Supplementing portal listings with video on social media expands your reach beyond portal-only browsers.",
    seeAlso: ["zoopla", "onthemarket", "property-portal"],
  },
  {
    term: "ROI (Return on Investment)",
    slug: "return-on-investment",
    definition:
      "A measure of the profitability of an investment, calculated as net profit divided by cost. For estate agents, the ROI of video marketing is measured by the additional instructions won and fees earned relative to the cost of the video platform.",
    relatedPage: { href: "/why-quiteright", label: "Why QuiteRight" },
  },

  // ── S ──────────────────────────────────────────────────────────────
  {
    term: "SEO (Search Engine Optimisation)",
    slug: "search-engine-optimisation",
    definition:
      "The practice of improving a website's visibility in search engine results. For estate agents, SEO helps their website and listings rank higher on Google for searches like 'estate agents in [location]'. Video content on a website improves dwell time and can boost search rankings.",
    seeAlso: ["dwell-time", "digital-marketing"],
  },
  {
    term: "Social Media Marketing",
    slug: "social-media-marketing",
    definition:
      "Using social platforms like Instagram, Facebook, TikTok, LinkedIn, and YouTube to promote properties and build brand awareness. Social media marketing is increasingly essential for estate agents, with video content driving the highest engagement rates across all platforms.",
    seeAlso: ["instagram-reels", "tiktok", "organic-reach"],
    relatedPage: { href: "/features", label: "See our features" },
  },
  {
    term: "Sole Agency",
    slug: "sole-agency",
    definition:
      "An agreement where one estate agent has the exclusive right to market a property for a set period. Sole agency fees are typically lower than multi-agency (usually 1\u20131.5% plus VAT). Most residential sales in the UK are conducted under sole agency agreements.",
    seeAlso: ["multi-agency", "joint-agency", "instruction"],
  },
  {
    term: "SSTC (Sold Subject to Contract)",
    slug: "sstc",
    definition:
      "A status indicating that a vendor has accepted an offer but the sale has not yet legally completed. The property remains SSTC until exchange of contracts. During this period, the sale can still fall through.",
    seeAlso: ["exchange-of-contracts", "completion"],
  },
  {
    term: "Stamp Duty (SDLT)",
    slug: "stamp-duty",
    definition:
      "Stamp Duty Land Tax is a tax paid by the buyer when purchasing a property in England and Northern Ireland above a certain threshold. The rate increases in bands based on the purchase price. Scotland has LBTT and Wales has LTT as equivalents.",
    seeAlso: ["first-time-buyer"],
  },

  // ── T ──────────────────────────────────────────────────────────────
  {
    term: "Tenant Referencing",
    slug: "tenant-referencing",
    definition:
      "The process of checking a prospective tenant's creditworthiness, employment status, identity, and rental history before granting a tenancy. Thorough referencing reduces the risk of rent arrears and property damage for landlords and letting agents.",
    seeAlso: ["assured-shorthold-tenancy", "deposit-protection"],
  },
  {
    term: "Thumbnail",
    slug: "thumbnail",
    definition:
      "The static preview image that represents a video before it is played. A compelling thumbnail is critical for video click-through rates. For property videos, the best thumbnails feature the most attractive room or exterior shot with a clear text overlay.",
    seeAlso: ["click-through-rate", "property-video"],
  },
  {
    term: "TikTok",
    slug: "tiktok",
    definition:
      "A short-form video platform popular with a wide demographic, including property buyers aged 25\u201344. TikTok's algorithm favours new content and can deliver significant organic reach. Estate agents are increasingly using TikTok to publish property video tours and behind-the-scenes content.",
    seeAlso: ["instagram-reels", "youtube-shorts"],
  },

  // ── U ──────────────────────────────────────────────────────────────
  {
    term: "Under Offer",
    slug: "under-offer",
    definition:
      "A property status indicating that a buyer has made an offer which the vendor is considering. Unlike SSTC, under offer does not necessarily mean the vendor has accepted. Agents may continue to arrange viewings while a property is under offer.",
    seeAlso: ["sstc", "offer"],
  },
  {
    term: "USP (Unique Selling Point)",
    slug: "unique-selling-point",
    definition:
      "The distinctive feature that makes a property or service stand out from the competition. In property marketing, identifying and highlighting a property's USP in video content is key to attracting the right buyer. For agents, offering video marketing is itself a USP.",
  },

  // ── V ──────────────────────────────────────────────────────────────
  {
    term: "Vendor",
    slug: "vendor",
    definition:
      "The person selling a property. In estate agency, the vendor is the agent's client. Understanding what vendors value \u2014 marketing quality, speed of sale, communication \u2014 is essential for winning and retaining instructions.",
    seeAlso: ["instruction", "market-appraisal"],
  },
  {
    term: "Video Render",
    slug: "video-render",
    definition:
      "The process of assembling a final video file from its component elements: images, text overlays, voiceover, music, and transitions. QuiteRight renders property videos in full HD (1080p) and delivers them ready to publish.",
    seeAlso: ["property-video"],
    relatedPage: { href: "/how-it-works", label: "How it works" },
  },
  {
    term: "Video Template",
    slug: "video-template",
    definition:
      "A pre-designed video layout with defined styles, transitions, and animations. Using templates allows agents to produce professional, on-brand property videos in seconds without design skills.",
    seeAlso: ["property-video", "brand-guidelines"],
    relatedPage: { href: "/features", label: "See our features" },
  },
  {
    term: "Video Tour",
    slug: "video-tour",
    definition:
      "A video that takes the viewer through a property room by room, creating a sense of walking through the space. Video tours can be filmed on-site or assembled from professional photographs with voiceover and music.",
    seeAlso: ["video-walkthrough", "property-video"],
    relatedPage: { href: "/how-it-works", label: "How it works" },
  },
  {
    term: "Video Walkthrough",
    slug: "video-walkthrough",
    definition:
      "Similar to a video tour but typically filmed in a single continuous take, giving a more natural, flowing view of the property. Walkthroughs are effective for showing the layout and flow of a home, and social media algorithms tend to favour this engaging format.",
    seeAlso: ["video-tour", "property-video"],
  },
  {
    term: "Viewings",
    slug: "viewings",
    definition:
      "Appointments where potential buyers or tenants visit a property in person. The goal of property marketing is to generate quality viewings with motivated buyers. Video pre-qualifies viewers by showing the property in detail before they visit, reducing wasted viewings.",
    seeAlso: ["open-house", "listing"],
  },
  {
    term: "Virtual Staging",
    slug: "virtual-staging",
    definition:
      "The digital addition of furniture and decor to photographs of empty rooms. Virtual staging helps buyers visualise how a property could look when furnished, without the cost of physical staging. It is an increasingly popular tool in property marketing.",
  },
  {
    term: "Voiceover",
    slug: "voiceover",
    definition:
      "A narration added to a video without the speaker appearing on screen. Professional voiceover elevates property videos from slideshows to polished marketing assets. QuiteRight offers curated British voiceover options in different styles and tones.",
    seeAlso: ["property-video"],
    relatedPage: { href: "/features", label: "See our features" },
  },

  // ── W ──────────────────────────────────────────────────────────────
  {
    term: "Watermark",
    slug: "watermark",
    definition:
      "A semi-transparent logo or text overlaid on a video or image to indicate ownership or branding. Estate agents typically watermark property photos to prevent unauthorised use. On QuiteRight paid plans, your agency branding replaces any platform watermark.",
  },

  // ── Y ──────────────────────────────────────────────────────────────
  {
    term: "Yield (Rental Yield)",
    slug: "rental-yield",
    definition:
      "A measure of the annual return on a buy-to-let investment, calculated as annual rental income divided by the property purchase price, expressed as a percentage. Rental yield is a key metric for investors and is often highlighted in letting agent marketing.",
  },

  // ── Z ──────────────────────────────────────────────────────────────
  {
    term: "Zoopla",
    slug: "zoopla",
    definition:
      "One of the UK's largest property portals, providing listings, valuations, and market data. Agents list on Zoopla to reach additional buyers alongside Rightmove and OnTheMarket.",
    seeAlso: ["rightmove", "onthemarket", "property-portal"],
  },
]

export const GLOSSARY_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")

export function findGlossaryTerm(slug: string): GlossaryTerm | undefined {
  return GLOSSARY_TERMS.find((t) => t.slug === slug)
}

export function glossaryTermLabel(slug: string): string | undefined {
  return findGlossaryTerm(slug)?.term
}
